import styled from '@emotion/styled';
import { CopyCheck, Pencil, Trash2 } from 'lucide-react';
import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { history } from 'store/store';
import { theme } from 'styles/theme/theme';
import EmptyButton from 'ud-ui/components/empty-button';
import { selectTemplates } from '../../../store/templates/selectors';
import { setActiveTemplate } from '../../../store/templates/templates';
import useDeleteModal from '../../hooks/modals/template/delete';
import useDuplicateModal from '../../hooks/modals/template/dublicate';
import useEditModal from '../../hooks/modals/template/edit';

interface IProps {
  template: Template;
  index: number;
}

function TemplateItem(props: IProps) {
  const dispatch = useDispatch();
  const { template, index } = props;

  const { data: feature } = useFetchAvailableFeature();

  const templates = useSelector(selectTemplates);
  const { modal: deleteModal, open: openDeleteModal } = useDeleteModal();
  const { modal: editModal, open: openEditModal } = useEditModal();
  const { modal: duplicateModal, open: openDuplicateModal } = useDuplicateModal();

  const onClick = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    history.push(Pages.TEMPLATE.builders.boards(template.id));
  }, [dispatch, template.id]);

  const onRename = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openEditModal();
  }, [dispatch, openEditModal, template.id]);

  const onDelete = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openDeleteModal();
  }, [dispatch, openDeleteModal, template.id]);

  const onDuplicate = useCallback(() => {
    dispatch(setActiveTemplate({ id: template.id }));
    openDuplicateModal();
  }, [dispatch, openDuplicateModal, template.id]);

  const isDefault = useMemo(() => {
    return template.access === 'default';
  }, [template.access]);

  const isCanDelete = useMemo(() => {
    return templates.length > 1 && !isDefault && feature?.OBS_INTEGRATION;
  }, [feature?.OBS_INTEGRATION, isDefault, templates.length]);

  const isCanDuplicate = useMemo(() => {
    return feature?.OBS_INTEGRATION;
  }, [feature?.OBS_INTEGRATION]);

  return (
    <CategoryItem
      key={template.name}
      style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F7F7F7' }}
      className={'flex justify-between px-6 py-5'}
    >
      <EmptyButton style={{ fontSize: 20 }} onClick={onClick}>
        {template.name}
      </EmptyButton>
      <div className={'flex'} style={{ gap: 24 }}>
        <EmptyButton icon={<CopyCheck size={20} />} disabled={!isCanDuplicate} onClick={onDuplicate} />
        <EmptyButton icon={<Pencil size={20} />} disabled={isDefault} onClick={onRename} />
        <EmptyButton color={theme.colors.CaseError} icon={<Trash2 size={20} />} disabled={!isCanDelete} onClick={onDelete} />
      </div>
      {deleteModal}
      {editModal}
      {duplicateModal}
    </CategoryItem>
  );
}

export default TemplateItem;

const CategoryItem = styled.div`
  font-family:
    SF Pro Text,
    sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
`;
